import { z } from 'zod';

/**
 * Geriye uyumluluk için pro_monthly/pro_yearly hâlâ enum olarak duruyor;
 * yeni dinamik plan slug'ları için createCheckoutSessionSchema.plan alanı
 * doğrudan string olarak da kabul ediyor (plans tablosundan slug lookup).
 */
export const billingPlanSchema = z.enum(['pro_monthly', 'pro_yearly']);
export type BillingPlan = z.infer<typeof billingPlanSchema>;

export const planSlugSchema = z
  .string()
  .min(3)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, 'Plan slug yalnızca küçük harf, rakam ve alt çizgi içermeli');

export const createCheckoutSessionSchema = z.object({
  plan: planSlugSchema,
});
export type CreateCheckoutSessionInput = z.infer<typeof createCheckoutSessionSchema>;

// Admin subscription mutations
export const adminAssignSubscriptionSchema = z.object({
  planSlug: planSlugSchema,
  trialDays: z.coerce.number().int().min(0).max(365).optional(),
});
export type AdminAssignSubscriptionInput = z.infer<typeof adminAssignSubscriptionSchema>;

export const adminCancelSubscriptionSchema = z.object({
  mode: z.enum(['now', 'period_end']).default('period_end'),
});
export type AdminCancelSubscriptionInput = z.infer<typeof adminCancelSubscriptionSchema>;

export const adminExtendTrialSchema = z.object({
  trialEnd: z.string().datetime(),
});
export type AdminExtendTrialInput = z.infer<typeof adminExtendTrialSchema>;

export const adminChangePlanSchema = z.object({
  newPlanSlug: planSlugSchema,
  prorationBehavior: z.enum(['create_prorations', 'none']).default('create_prorations'),
});
export type AdminChangePlanInput = z.infer<typeof adminChangePlanSchema>;

// Coupons (Stripe canonical)
export const couponDurationSchema = z.enum(['once', 'repeating', 'forever']);
export type CouponDuration = z.infer<typeof couponDurationSchema>;

export const adminCreateCouponSchema = z
  .object({
    name: z.string().min(1).max(120),
    percentOff: z.coerce.number().int().min(1).max(100).optional(),
    amountOff: z.coerce.number().int().min(1).optional(),
    currency: z.string().length(3).optional(),
    duration: couponDurationSchema,
    durationInMonths: z.coerce.number().int().min(1).max(120).optional(),
    maxRedemptions: z.coerce.number().int().min(1).optional(),
  })
  .refine((d) => (d.percentOff && !d.amountOff) || (!d.percentOff && d.amountOff), {
    message: 'percentOff veya amountOff (currency ile) — tam olarak biri olmalı',
    path: ['percentOff'],
  })
  .refine((d) => d.duration !== 'repeating' || (d.durationInMonths ?? 0) > 0, {
    message: 'repeating için durationInMonths zorunlu',
    path: ['durationInMonths'],
  })
  .refine((d) => !d.amountOff || (d.currency && d.currency.length === 3), {
    message: 'amountOff varsa currency zorunlu',
    path: ['currency'],
  });
export type AdminCreateCouponInput = z.infer<typeof adminCreateCouponSchema>;

export const adminCreatePromotionCodeSchema = z.object({
  code: z
    .string()
    .min(3)
    .max(40)
    .regex(/^[A-Z0-9_-]+$/i, 'Yalnızca harf, rakam, tire ve alt çizgi'),
  expiresAt: z.string().datetime().optional(),
  maxRedemptions: z.coerce.number().int().min(1).optional(),
});
export type AdminCreatePromotionCodeInput = z.infer<typeof adminCreatePromotionCodeSchema>;

export const adminListInvoicesQuerySchema = z.object({
  status: z.enum(['draft', 'open', 'paid', 'uncollectible', 'void']).optional(),
  customerEmail: z.string().email().optional(),
  startingAfter: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type AdminListInvoicesQuery = z.infer<typeof adminListInvoicesQuerySchema>;

export const adminListCouponsQuerySchema = z.object({
  startingAfter: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type AdminListCouponsQuery = z.infer<typeof adminListCouponsQuerySchema>;

export const adminCreateRefundSchema = z.object({
  paymentIntentId: z.string().startsWith('pi_'),
  amount: z.coerce.number().int().positive().optional(),
  reason: z.enum(['duplicate', 'fraudulent', 'requested_by_customer']).optional(),
});
export type AdminCreateRefundInput = z.infer<typeof adminCreateRefundSchema>;
